/**
 * Premium "Partner Service Detail" — Allyver-Produkt-/Service-Detailseite.
 *
 * Drei voll ausgearbeitete Layout-Varianten — alle teilen sich Daten +
 * Backdrop, unterscheiden sich aber komplett in Komposition, Typo-Hierarchie
 * und Animation. Auswahl per `inputs.extras.layout`:
 *
 *   1. "stacked"    — Allyver-Style vertikales Layout (Default). Bild oben mit
 *                     Blur-Backdrop, dann Tag → Titel → Preis → Verkäufer →
 *                     Beschreibung → CTA. Clean, all-purpose.
 *
 *   2. "cinematic"  — Full-Bleed Bild (75 % der Höhe) als Hero mit dezentem
 *                     dunklen Verlauf nach unten. Kategorie-Pill oben links,
 *                     riesiger Preis-Badge oben rechts, Titel + CTA als
 *                     Glasleiste unten. Movie-Poster-Vibes.
 *
 *   3. "editorial"  — Magazine-/Print-Look: zentriertes Bild in Polaroid-
 *                     Akzent-Frame, **mega**-Preis als Hero darüber, Titel
 *                     als Serif-Subtitle, Beschreibung in zweispaltigem
 *                     Editorial-Setting, Footer-Linie mit Anbieter + Standort.
 *                     Hochwertig, ruhig, sehr lesbar.
 *
 * Inhalte können entweder per Inspector manuell gesetzt werden ODER per
 * Allyver-Produkt-Link automatisch befüllt werden (Endpoint
 * `/api/partner-product-extract`).
 *
 * 9:16-first; degradiert clean auf 1:1 / 16:9 (etwas mehr Whitespace).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import type { Fragment } from "../../types.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, sceneSfxAudio, type SceneSfxBeat, typeSize, weights, withAlpha,
  colors as themeColors,
} from "../../reel/sceneShared.js";

type LayoutId = "stacked" | "cinematic" | "editorial" | "card" | "split";

function resolveLayout(raw: unknown): LayoutId {
  const s = String(raw ?? "stacked").trim().toLowerCase();
  if (s === "cinematic" || s === "hero") return "cinematic";
  if (s === "editorial" || s === "magazine") return "editorial";
  if (s === "card" || s === "allyver" || s === "glass") return "card";
  if (s === "split" || s === "showcase") return "split";
  return "stacked";
}

/**
 * Großzügige Safe-Zone, die alle gängigen Plattformen (TikTok / Reels /
 * Shorts / Stories) abdeckt. Maxwerte aus `src/hyperframes/format.ts`.
 * Wir kennen die konkrete Plattform im Scene-Builder nicht, also nehmen wir
 * das Worst-Case-Inset — Bilder/Backdrops dürfen ins Unsafe-Area lecken,
 * Text/Pills/CTA bleiben strikt drin.
 */
function getSafeInsets(format: "9:16" | "1:1" | "16:9"): {
  top: number; bottom: number; left: number; right: number;
} {
  if (format === "9:16") return { top: 260, bottom: 460, left: 80, right: 220 };
  if (format === "1:1") return { top: 120, bottom: 120, left: 120, right: 120 };
  return { top: 110, bottom: 110, left: 150, right: 150 };
}

export const partnerServiceDetail: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = inputs.accent ? accentFor(inputs) : themeColors.primary;
  const isVertical = ctx.format === "9:16";

  // Content (shared by all layouts)
  const title = (inputs.text ?? "Seedos All-in-One Taste Box").trim();
  const sellerName = (inputs.subtext ?? "Seedos GmbH").trim();
  const priceLabel = String(inputs.extras?.price ?? inputs.unit ?? "21,19 €").trim();
  const locationLabel = String(inputs.extras?.location ?? "").trim();
  const kategorieTag = String(inputs.extras?.kategorieType ?? "PRODUKT").trim().toUpperCase();
  const description = String(inputs.extras?.description ?? "").trim();
  const ctaText = String(inputs.extras?.ctaText ?? "Jetzt anfragen").trim();
  const url = (inputs.url ?? "allyver.com").trim();
  const layout = resolveLayout(inputs.extras?.layout);
  const imageFit = String(inputs.extras?.imageFit ?? (layout === "cinematic" ? "cover" : "contain"))
    .trim()
    .toLowerCase() === "cover" ? "cover" : "contain";

  // media[0] = product image, media[1] = seller logo (optional)
  const productImg = pickMediaSrc(inputs.media, 0);
  const sellerLogo = pickMediaSrc(inputs.media, 1);

  const t = makeTl(clipStartSec);

  // Description short-cutting (used by stacked/editorial/card/split)
  // Längere Karten brauchen mehr Text; visuelles Clamp via -webkit-line-clamp
  // + Ellipsis greift trotzdem, falls die Zeilenanzahl pro Layout enger ist.
  const descShort = (() => {
    if (!description) return "";
    const flat = description.replace(/\s+/g, " ").trim();
    const MAX = isVertical ? 320 : 380;
    if (flat.length <= MAX) return flat;
    const cut = flat.slice(0, MAX);
    return cut.replace(/[\s.,;:]+\S*$/, "") + "…";
  })();

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, {
    variant: layout === "cinematic" ? "cinematic" : "studio",
    accent,
  });

  // --- Shared building blocks --------------------------------------------
  const buildProductImage = (id: string, h: number): string => {
    if (!productImg) {
      return `<div id="${id}" style="position:relative;width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${h * 0.32}px;letter-spacing:-3px;color:${withAlpha(themeColors.fg, 0.18)};background:linear-gradient(135deg, ${withAlpha(accent, 0.18)} 0%, ${withAlpha(themeColors.fg, 0.04)} 100%);">${escapeHtml(title.charAt(0))}</div>`;
    }
    if (imageFit === "contain") {
      return `<div style="position:absolute;inset:0;background-image:url('${escapeHtml(productImg)}');background-size:cover;background-position:center;filter:blur(28px) brightness(0.55) saturate(1.15);transform:scale(1.18);z-index:0;pointer-events:none;"></div>
<img id="${id}" src="${escapeHtml(productImg)}" alt="${escapeHtml(title)}" style="position:relative;z-index:1;width:100%;height:100%;object-fit:contain;display:block;" />`;
    }
    return `<img id="${id}" src="${escapeHtml(productImg)}" alt="${escapeHtml(title)}" style="position:relative;width:100%;height:100%;object-fit:cover;display:block;" />`;
  };

  // Each layout returns a Fragment-like {html, init, tweens}.
  const layoutCtx: LayoutCtx = {
    clipPrefix, isVertical, accent, ctx, t, title, sellerName, priceLabel,
    locationLabel, kategorieTag, descShort, ctaText, url, sellerLogo,
    buildProductImage, productImg,
  };
  const built =
    layout === "cinematic" ? buildCinematic(layoutCtx)
    : layout === "editorial" ? buildEditorial(layoutCtx)
    : layout === "card" ? buildCard(layoutCtx)
    : layout === "split" ? buildSplit(layoutCtx)
    : buildStacked(layoutCtx);

  built.tweens!.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  // Dezente, layout-spezifische SFX-Begleitung (siehe `SFX_BEATS_BY_LAYOUT`).
  // Volume bleibt bewusst im Bereich 0.16–0.30 ("eher leise, eher Apple-/
  // Linear-UI-Sound, eher Atmo als Knaller"). Wird stumm geschaltet, sobald
  // der Nutzer im Inspector / Layers-Panel `extras.sfx = false` setzt.
  const ctaAtForLayout =
    layout === "cinematic" ? 1.95
    : layout === "editorial" ? 1.55
    : layout === "card" ? (descShort ? 1.9 : 1.7)
    : layout === "split" ? (descShort ? 2.05 : 1.8)
    : /* stacked */ (descShort ? 1.7 : 1.45);
  const sfxBeats = buildLayoutSfxBeats(layout, ctaAtForLayout);
  const sfxHtml = sceneSfxAudio({ clipPrefix, clipStartSec, inputs, beats: sfxBeats });
  if (sfxHtml) {
    built.html = `${built.html}\n${sfxHtml}`;
  }

  return combineSceneFragments([backdrop, built]);
};

/**
 * Pro Layout eine kleine, ruhig gesetzte SFX-Choreografie. Bewusst sparsam —
 * 3–4 Beats pro Szene, alle leise, keine Impacts oder Wooshes. Sounds:
 *   • `tap` / `tick`  → Karte/Stempel landet, Mini-Akzent
 *   • `glass`         → Preis-Reveal (klar, sauber, kein Knall)
 *   • `shimmer`       → Sweep, Edge-Line, Editorial-Hero-Preis
 *   • `pop`           → CTA-Pille kommt rein
 */
function buildLayoutSfxBeats(layout: LayoutId, ctaAt: number): SceneSfxBeat[] {
  if (layout === "cinematic") {
    return [
      { sound: "shimmer", atSec: 0.55, volume: 0.16, durSec: 1.2 },
      { sound: "tap",     atSec: 0.78, volume: 0.20, durSec: 0.6 },
      { sound: "glass",   atSec: 1.22, volume: 0.22, durSec: 0.9 },
      { sound: "pop",     atSec: ctaAt + 0.02, volume: 0.26, durSec: 0.5 },
    ];
  }
  if (layout === "editorial") {
    return [
      { sound: "shimmer", atSec: 0.38, volume: 0.18, durSec: 1.3 },
      { sound: "tap",     atSec: 0.92, volume: 0.18, durSec: 0.5 },
      { sound: "pop",     atSec: ctaAt + 0.02, volume: 0.24, durSec: 0.5 },
    ];
  }
  if (layout === "card") {
    return [
      { sound: "tap",   atSec: 0.55, volume: 0.18, durSec: 0.5 },
      { sound: "tick",  atSec: 0.72, volume: 0.22, durSec: 0.3 },
      { sound: "glass", atSec: 1.18, volume: 0.22, durSec: 0.9 },
      { sound: "pop",   atSec: ctaAt + 0.02, volume: 0.26, durSec: 0.5 },
    ];
  }
  if (layout === "split") {
    return [
      { sound: "tap",     atSec: 0.38, volume: 0.16, durSec: 0.5 },
      { sound: "shimmer", atSec: 0.62, volume: 0.18, durSec: 1.0 },
      { sound: "glass",   atSec: 1.32, volume: 0.22, durSec: 0.9 },
      { sound: "pop",     atSec: ctaAt + 0.02, volume: 0.26, durSec: 0.5 },
    ];
  }
  // stacked (default)
  return [
    { sound: "tap",   atSec: 0.12, volume: 0.18, durSec: 0.5 },
    { sound: "tick",  atSec: 0.58, volume: 0.18, durSec: 0.3 },
    { sound: "glass", atSec: 0.92, volume: 0.22, durSec: 0.9 },
    { sound: "pop",   atSec: ctaAt + 0.02, volume: 0.26, durSec: 0.5 },
  ];
}

// ============================================================================
// SHARED TYPES
// ============================================================================
type LayoutCtx = {
  clipPrefix: string;
  isVertical: boolean;
  accent: string;
  ctx: Parameters<SceneBuilder>[0]["ctx"];
  t: ReturnType<typeof makeTl>;
  title: string;
  sellerName: string;
  priceLabel: string;
  locationLabel: string;
  kategorieTag: string;
  descShort: string;
  ctaText: string;
  url: string;
  sellerLogo: string | undefined;
  productImg: string | undefined;
  buildProductImage: (id: string, h: number) => string;
};

// ============================================================================
// LAYOUT 1 — STACKED (Allyver-style)
// ============================================================================
function buildStacked(L: LayoutCtx): Fragment {
  const { clipPrefix, isVertical, accent, ctx, t, title, sellerName, priceLabel,
    locationLabel, kategorieTag, descShort, ctaText, url, sellerLogo,
    buildProductImage } = L;

  const cardW = isVertical ? 920 : 1180;
  const imgH  = isVertical ? 520 : 380;
  const titleSize = typeSize(isVertical ? "lg" : "md", ctx.format) * 0.95;
  const priceSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.42;
  const sellerSize = typeSize("sm", ctx.format) * 0.82;
  const tagSize   = typeSize("xs", ctx.format) * 0.92;
  const descSize  = typeSize("sm", ctx.format) * 0.78;
  const ctaSize   = typeSize("sm", ctx.format) * 0.85;
  const locSize   = typeSize("sm", ctx.format) * 0.72;
  const sellerAvatarSize = isVertical ? 52 : 46;

  const ids = {
    card: `${clipPrefix}-card`, imgWrap: `${clipPrefix}-imgw`, img: `${clipPrefix}-img`,
    imgGlow: `${clipPrefix}-iglow`, tag: `${clipPrefix}-tag`, title: `${clipPrefix}-title`,
    price: `${clipPrefix}-price`, seller: `${clipPrefix}-seller`, loc: `${clipPrefix}-loc`,
    desc: `${clipPrefix}-desc`, cta: `${clipPrefix}-cta`, ctaGlow: `${clipPrefix}-cglow`,
  };

  const sellerAvatarHtml = sellerLogo
    ? `<img src="${escapeHtml(sellerLogo)}" alt="seller" style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:12px;object-fit:contain;background:${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};padding:4px;" />`
    : `<div style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:12px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.65)} 100%);display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sellerAvatarSize * 0.45}px;color:${themeColors.bg};">${escapeHtml(sellerName.charAt(0))}</div>`;

  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:stretch;justify-content:center;gap:${isVertical ? 18 : 14}px;padding:${isVertical ? 70 : 50}px ${isVertical ? 60 : 90}px;">
    <div id="${ids.card}" style="display:flex;flex-direction:column;align-items:stretch;gap:${isVertical ? 18 : 14}px;width:100%;max-width:${cardW}px;margin:0 auto;">
      <div id="${ids.imgWrap}" style="position:relative;width:100%;height:${imgH}px;border-radius:24px;overflow:hidden;background:${withAlpha(themeColors.fg, 0.04)};border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};box-shadow:0 30px 70px rgba(0,0,0,0.5);">
        <div id="${ids.imgGlow}" style="position:absolute;inset:-2px;border-radius:24px;background:linear-gradient(135deg, ${withAlpha(accent, 0.55)} 0%, transparent 40%, transparent 60%, ${withAlpha(accent, 0.4)} 100%);opacity:0;mix-blend-mode:screen;pointer-events:none;"></div>
        ${buildProductImage(ids.img, imgH)}
      </div>
      <div id="${ids.tag}" style="display:inline-flex;align-self:flex-start;align-items:center;gap:8px;padding:6px 14px;border-radius:9999px;background:${withAlpha(accent, 0.18)};border:1px solid ${withAlpha(accent, 0.5)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${tagSize}px;letter-spacing:3px;text-transform:uppercase;color:${accent};">
        <span style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:${accent};box-shadow:0 0 10px ${accent};"></span>
        <span>${escapeHtml(kategorieTag)}</span>
      </div>
      <div id="${ids.title}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.1;letter-spacing:-1px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(title)}</div>
      <div id="${ids.price}" style="position:relative;display:inline-block;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${priceSize}px;line-height:1;letter-spacing:-1.5px;background:linear-gradient(135deg, ${accent} 0%, ${accent} 55%, ${withAlpha(accent, 0.78)} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;padding:2px 0;">
        ${escapeHtml(priceLabel)}
        <span style="position:absolute;left:0;bottom:-4px;width:48px;height:3px;border-radius:9999px;background:linear-gradient(90deg, ${accent}, ${withAlpha(themeColors.fg, 0.4)});box-shadow:0 0 12px ${withAlpha(accent, 0.55)};"></span>
      </div>
      ${locationLabel ? `<div id="${ids.loc}" style="display:inline-flex;align-items:center;gap:6px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${locSize}px;color:${withAlpha(themeColors.fg, 0.7)};">
        <svg width="${Math.round(locSize * 1.05)}" height="${Math.round(locSize * 1.05)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        <span>${escapeHtml(locationLabel)}</span>
      </div>` : ""}
      <div id="${ids.seller}" style="display:flex;align-items:center;gap:12px;padding:10px 14px;border-radius:16px;background:${withAlpha(themeColors.fg, 0.05)};border:1px solid ${withAlpha(themeColors.fg, 0.12)};">
        ${sellerAvatarHtml}
        <div style="display:flex;flex-direction:column;gap:2px;min-width:0;">
          <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sellerSize}px;color:${themeColors.fg};line-height:1.1;">${escapeHtml(sellerName)}</div>
          <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${locSize}px;color:${withAlpha(themeColors.fg, 0.6)};letter-spacing:1.4px;text-transform:uppercase;">Anbieter</div>
        </div>
      </div>
      ${descShort ? `<div id="${ids.desc}" style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${descSize}px;line-height:1.45;color:${withAlpha(themeColors.fg, 0.78)};text-wrap:balance;">${escapeHtml(descShort)}</div>` : ""}
      <div id="${ids.cta}" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:${isVertical ? 18 : 14}px ${isVertical ? 34 : 28}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 16px 42px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2px;text-transform:uppercase;color:${themeColors.bg};align-self:center;">
        <div id="${ids.ctaGlow}" style="position:absolute;inset:-12px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
        <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
        <span>${escapeHtml(ctaText)}</span>
      </div>
    </div>
  </div>`;
  void url;

  const initLines: string[] = [
    gsapSetLine(`#${ids.card}`, { opacity: 0, y: 24 }),
    gsapSetLine(`#${ids.imgWrap}`, { opacity: 0, scale: 0.94 }),
    gsapSetLine(`#${ids.imgGlow}`, { opacity: 0 }),
    gsapSetLine(`#${ids.tag}`, { opacity: 0, y: 10 }),
    gsapSetLine(`#${ids.title}`, { opacity: 0, y: 14 }),
    gsapSetLine(`#${ids.price}`, { opacity: 0, y: 18, scale: 0.96 }),
    gsapSetLine(`#${ids.seller}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${ids.cta}`, { opacity: 0, y: 18, scale: 0.92 }),
    gsapSetLine(`#${ids.ctaGlow}`, { opacity: 0 }),
  ];
  if (locationLabel) initLines.push(gsapSetLine(`#${ids.loc}`, { opacity: 0, y: 10 }));
  if (descShort)     initLines.push(gsapSetLine(`#${ids.desc}`, { opacity: 0, y: 10 }));

  const tweens: string[] = [
    t.to(`#${ids.card}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${ids.imgWrap}`, { opacity: 1, scale: 1, duration: 0.75, ease: "raw:\"power3.out\"" }, 0.1),
    t.fromTo(`#${ids.imgGlow}`, { opacity: 0 }, { opacity: 0.6, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${ids.imgGlow}`, { opacity: 0.18, duration: 0.7, ease: "raw:\"power2.inOut\"" }, 0.7),
    t.to(`#${ids.tag}`,   { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 0.55),
    t.to(`#${ids.title}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.7),
    t.to(`#${ids.price}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.6)\"" }, 0.9),
    t.to(`#${ids.seller}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 1.15),
  ];
  if (locationLabel) tweens.push(t.to(`#${ids.loc}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 1.05));
  if (descShort)     tweens.push(t.to(`#${ids.desc}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.35));
  const ctaAt = descShort ? 1.7 : 1.45;
  tweens.push(t.to(`#${ids.cta}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, ctaAt));
  tweens.push(t.to(`#${ids.ctaGlow}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, ctaAt + 0.1));
  tweens.push(t.to(`#${ids.ctaGlow}`, { opacity: 0.4, duration: 1.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 0.7));

  return { html, init: initLines.join("\n"), tweens };
}

// ============================================================================
// LAYOUT 2 — CINEMATIC (Movie-poster style, full-bleed hero)
// ============================================================================
function buildCinematic(L: LayoutCtx): Fragment {
  const { clipPrefix, accent, ctx, t, title, sellerName, priceLabel,
    locationLabel, kategorieTag, ctaText, url, sellerLogo,
    buildProductImage } = L;

  const safe = getSafeInsets(ctx.format);

  // --- Komposition ---------------------------------------------------------
  // Harte vertikale Trennung: oben Bildzone, unten dunkles Panel.
  // Es liegt KEIN Text mehr über dem Bild — das Bild atmet in seiner eigenen
  // Zone, das Panel hat einen massiven dunklen Hintergrund.
  const canvasH = ctx.format === "9:16" ? 1920 : 1080;
  // Bildhöhe ~48 % bei 9:16, ~50 % bei 1:1, ~50 % bei 16:9.
  const imageH =
    ctx.format === "9:16" ? 920 : ctx.format === "1:1" ? 540 : 520;
  const panelTop = imageH;
  const panelH = canvasH - panelTop;

  // Reels-optimierte Anker (häufigste Plattform). Etwas aggressiver als
  // die pauschalen Worst-Case-Insets, dafür viel weniger toter Raum.
  // Inhalte bleiben in der Reels-Safe-Zone (top 240 / bottom 420).
  const pillTopY = ctx.format === "9:16" ? 240
                 : ctx.format === "1:1" ? 100
                 : 90;
  const bottomAnchor = ctx.format === "9:16" ? 420
                     : ctx.format === "1:1" ? 100
                     : 90;

  // --- Typography (feste px, kein Skalen-Drift) ----------------------------
  const tagSize        = ctx.format === "9:16" ? 22 : 18;
  const priceLabelSize = ctx.format === "9:16" ? 22 : 18;
  // Adaptiv: "Preis auf Anfrage" (17 chars) bekommt kleinere Schrift als "21 €" (4).
  const pLen = priceLabel.length;
  const priceSize =
    ctx.format === "9:16"
      ? (pLen > 14 ? 88 : pLen > 8 ? 124 : 152)
      : (pLen > 14 ? 64 : pLen > 8 ? 90 : 118);
  const titleSize  = ctx.format === "9:16" ? 56 : 42;
  const metaSize   = ctx.format === "9:16" ? 26 : 22;
  const ctaSize    = ctx.format === "9:16" ? 24 : 20;
  const sellerAvatarSize = metaSize * 1.35;
  void url;

  // Panel-Innenabstände — Bottom = Bottom-Safe, damit Inhalt nicht in die
  // unsichere Zone rutscht. Top = kleiner Atemraum unterhalb der Accent-Stripe.
  const panelPadTop = ctx.format === "9:16" ? 56 : 38;
  const panelPadX   = safe.left;

  const ids = {
    bg:        `${clipPrefix}-bg`,
    hero:      `${clipPrefix}-hero`,
    img:       `${clipPrefix}-img`,
    imgFade:   `${clipPrefix}-imgfade`,
    sweep:     `${clipPrefix}-sweep`,
    tag:       `${clipPrefix}-tag`,
    panel:     `${clipPrefix}-panel`,
    stripe:    `${clipPrefix}-stripe`,
    priceLbl:  `${clipPrefix}-plabel`,
    price:     `${clipPrefix}-price`,
    title:     `${clipPrefix}-title`,
    meta:      `${clipPrefix}-meta`,
    cta:       `${clipPrefix}-cta`,
    ctaGlow:   `${clipPrefix}-cglow`,
  };

  const html = `<div id="${ids.bg}" style="position:absolute;inset:0;background:#06080c;">

    <!-- BILDZONE (eigenständig, kein Text liegt darüber) -->
    <div id="${ids.hero}" style="position:absolute;top:0;left:0;right:0;height:${imageH}px;overflow:hidden;z-index:1;">
      ${buildProductImage(ids.img, imageH)}
      <!-- Sanfter Übergang Bild → Panel, damit der harte Schnitt cinematisch wirkt -->
      <div id="${ids.imgFade}" style="position:absolute;left:0;right:0;bottom:0;height:${Math.round(imageH * 0.32)}px;background:linear-gradient(180deg, transparent 0%, rgba(6,8,12,0.55) 55%, rgba(6,8,12,1) 100%);pointer-events:none;z-index:2;"></div>
      <!-- Premium light sweep -->
      <div id="${ids.sweep}" style="position:absolute;top:-25%;left:-60%;width:55%;height:160%;transform:rotate(18deg);background:linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 45%, rgba(255,255,255,0.42) 50%, rgba(255,255,255,0.18) 55%, transparent 100%);filter:blur(2px);opacity:0;pointer-events:none;z-index:3;"></div>

      <!-- Kategorie-Chip (top-left, Reels-Safe-Line) -->
      <div id="${ids.tag}" style="position:absolute;top:${pillTopY}px;left:${safe.left}px;z-index:5;display:inline-flex;align-items:center;gap:10px;padding:10px 20px;border-radius:9999px;background:rgba(6,8,12,0.62);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border:1px solid ${withAlpha(accent, 0.5)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${tagSize}px;letter-spacing:3px;text-transform:uppercase;color:${accent};max-width:calc(100% - ${safe.left + safe.right + 16}px);">
        <span style="display:inline-block;width:7px;height:7px;border-radius:9999px;background:${accent};box-shadow:0 0 14px ${accent};animation:hyper-blink 1.6s ease-in-out infinite;flex:0 0 auto;"></span>
        <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;">${escapeHtml(kategorieTag)}</span>
      </div>
    </div>

    <!-- DARK PANEL (eigene massive Hintergrundfläche — Bild kann hier nicht durchscheinen) -->
    <div id="${ids.panel}" style="position:absolute;left:0;right:0;top:${panelTop}px;height:${panelH}px;z-index:4;background:linear-gradient(180deg, #0a0d12 0%, #06080c 60%, #04060a 100%);overflow:hidden;">

      <!-- Accent-Stripe ganz oben am Panel -->
      <div id="${ids.stripe}" style="position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg, transparent 0%, ${accent} 25%, ${accent} 75%, transparent 100%);box-shadow:0 0 24px ${withAlpha(accent, 0.85)};transform-origin:50% 50%;"></div>

      <!-- TOP-BLOCK: Preis-Label + Hero-Preis + Titel — absolut am oberen Panel-Rand -->
      <div id="${clipPrefix}-topblock" style="position:absolute;top:${panelPadTop}px;left:${panelPadX}px;right:${panelPadX}px;display:flex;flex-direction:column;gap:${ctx.format === "9:16" ? 14 : 10}px;">
        <div id="${ids.priceLbl}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${priceLabelSize}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">
          <span style="display:inline-block;width:32px;height:2px;background:${accent};box-shadow:0 0 12px ${withAlpha(accent, 0.7)};"></span>
          <span>Preis</span>
        </div>
        <div id="${ids.price}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${priceSize}px;line-height:0.95;letter-spacing:-3px;background:linear-gradient(135deg, ${accent} 0%, ${accent} 45%, ${themeColors.fg} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-wrap:balance;max-width:100%;overflow-wrap:break-word;text-shadow:0 0 60px ${withAlpha(accent, 0.25)};">${escapeHtml(priceLabel)}</div>
        <div id="${ids.title}" style="margin-top:${ctx.format === "9:16" ? 6 : 4}px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.08;letter-spacing:-1.4px;color:${themeColors.fg};display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;text-wrap:balance;">${escapeHtml(title)}</div>
      </div>

      <!-- BOTTOM-BLOCK: Meta + CTA + URL — absolut am unteren Panel-Rand, knapp oberhalb der Bottom-Safe-Line -->
      <div id="${clipPrefix}-bottomblock" style="position:absolute;bottom:${bottomAnchor}px;left:${panelPadX}px;right:${panelPadX}px;display:flex;flex-direction:column;gap:${ctx.format === "9:16" ? 16 : 12}px;">
        <div id="${ids.meta}" style="display:flex;align-items:center;gap:14px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${metaSize}px;color:${withAlpha(themeColors.fg, 0.78)};flex-wrap:wrap;">
          ${sellerLogo
            ? `<img src="${escapeHtml(sellerLogo)}" alt="seller" style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:8px;object-fit:contain;background:${withAlpha(themeColors.fg, 0.08)};padding:3px;flex:0 0 auto;" />`
            : `<span style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:8px;background:linear-gradient(135deg, ${accent}, ${withAlpha(accent, 0.6)});display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-weight:${weights.black};font-size:${sellerAvatarSize * 0.5}px;flex:0 0 auto;">${escapeHtml(sellerName.charAt(0))}</span>`}
          <span style="color:${themeColors.fg};">${escapeHtml(sellerName)}</span>
          ${locationLabel ? `<span style="opacity:0.4;">·</span>
          <span style="display:inline-flex;align-items:center;gap:6px;">
            <svg width="${Math.round(metaSize * 1.05)}" height="${Math.round(metaSize * 1.05)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>${escapeHtml(locationLabel)}</span>
          </span>` : ""}
        </div>

        <div id="${ids.cta}" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:${ctx.format === "9:16" ? 16 : 12}px ${ctx.format === "9:16" ? 28 : 22}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 18px 46px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2.2px;text-transform:uppercase;color:${themeColors.bg};align-self:flex-start;max-width:100%;">
          <div id="${ids.ctaGlow}" style="position:absolute;inset:-14px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.55)} 0%, transparent 70%);filter:blur(10px);pointer-events:none;opacity:0;"></div>
          <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          <span style="white-space:nowrap;">${escapeHtml(ctaText)}</span>
        </div>
      </div>
    </div>
  </div>
<style>@keyframes hyper-blink{0%,100%{opacity:0.4;transform:scale(0.85);}50%{opacity:1;transform:scale(1.05);}}</style>`;

  const init = [
    gsapSetLine(`#${ids.hero}`, { opacity: 0 }),
    gsapSetLine(`#${ids.img}`, { scale: 1.12 }),
    gsapSetLine(`#${ids.tag}`, { opacity: 0, x: -30 }),
    gsapSetLine(`#${ids.panel}`, { yPercent: 100 }),
    gsapSetLine(`#${ids.stripe}`, { scaleX: 0 }),
    gsapSetLine(`#${ids.priceLbl}`, { opacity: 0, y: 14 }),
    gsapSetLine(`#${ids.price}`, { opacity: 0, y: 26, scale: 0.92 }),
    gsapSetLine(`#${ids.title}`, { opacity: 0, y: 18 }),
    gsapSetLine(`#${ids.meta}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${ids.cta}`, { opacity: 0, y: 20, scale: 0.92 }),
    gsapSetLine(`#${ids.ctaGlow}`, { opacity: 0 }),
    gsapSetLine(`#${ids.sweep}`, { opacity: 0, x: -200 }),
  ].join("\n");

  const tweens: string[] = [
    // Bild rein + sanfter Ken-Burns
    t.to(`#${ids.hero}`, { opacity: 1, duration: 0.55, ease: "raw:\"power2.out\"" }, 0),
    t.to(`#${ids.img}`,  { scale: 1.0, duration: 6.0, ease: "raw:\"power1.out\"" }, 0),
    // Kategorie-Chip
    t.to(`#${ids.tag}`,  { opacity: 1, x: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.35),
    // Sweep über das Bild
    t.to(`#${ids.sweep}`, { opacity: 1, duration: 0.05, ease: "raw:\"none\"" }, 0.55),
    t.to(`#${ids.sweep}`, { x: 1400, duration: 0.85, ease: "raw:\"power2.inOut\"" }, 0.55),
    t.to(`#${ids.sweep}`, { opacity: 0, duration: 0.25, ease: "raw:\"power1.in\"" }, 1.25),
    // Panel hochfahren — dramatischer Cinematic-Reveal
    t.to(`#${ids.panel}`, { yPercent: 0, duration: 0.85, ease: "raw:\"power4.out\"" }, 0.7),
    t.fromTo(`#${ids.stripe}`, { scaleX: 0 }, { scaleX: 1, duration: 0.65, ease: "raw:\"power3.out\"" }, 0.95),
    // Panel-Inhalt staffeln
    t.to(`#${ids.priceLbl}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 1.05),
    t.to(`#${ids.price}`,    { opacity: 1, y: 0, scale: 1, duration: 0.75, ease: "raw:\"back.out(1.5)\"" }, 1.2),
    t.to(`#${ids.title}`,    { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power3.out\"" }, 1.5),
    t.to(`#${ids.meta}`,     { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.75),
    t.to(`#${ids.cta}`,      { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 1.95),
    t.to(`#${ids.ctaGlow}`,  { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, 2.05),
    t.to(`#${ids.ctaGlow}`,  { opacity: 0.4, duration: 1.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 2.7),
  ];

  return { html, init, tweens };
}

// ============================================================================
// LAYOUT 3 — EDITORIAL (Magazine spread, polaroid frame, mega-price hero)
// ============================================================================
function buildEditorial(L: LayoutCtx): Fragment {
  const { clipPrefix, isVertical, accent, ctx, t, title, sellerName, priceLabel,
    locationLabel, kategorieTag, descShort, ctaText, url, sellerLogo,
    buildProductImage } = L;
  void url;

  const cardW = isVertical ? 880 : 1080;
  const imgH  = isVertical ? 460 : 360;
  const eyebrowSize  = typeSize("xs", ctx.format) * 0.95;
  const megaPriceSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.95;
  const titleSize     = typeSize(isVertical ? "md" : "sm", ctx.format) * 0.88;
  const descSize      = typeSize("sm", ctx.format) * 0.78;
  const ctaSize       = typeSize("sm", ctx.format) * 0.82;
  const footSize      = typeSize("sm", ctx.format) * 0.7;

  const ids = {
    page:  `${clipPrefix}-page`,
    eyebrow: `${clipPrefix}-eye`,
    rule:  `${clipPrefix}-rule`,
    price: `${clipPrefix}-price`,
    priceUnderline: `${clipPrefix}-pul`,
    title: `${clipPrefix}-title`,
    frame: `${clipPrefix}-frame`,
    framePaper: `${clipPrefix}-paper`,
    img:   `${clipPrefix}-img`,
    desc:  `${clipPrefix}-desc`,
    foot:  `${clipPrefix}-foot`,
    footRule: `${clipPrefix}-frule`,
    cta:   `${clipPrefix}-cta`,
    ctaGlow: `${clipPrefix}-cglow`,
  };

  const safe = getSafeInsets(ctx.format);

  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:${safe.top}px ${Math.max(safe.left, isVertical ? 50 : 90)}px ${safe.bottom}px ${Math.max(safe.right, isVertical ? 50 : 90)}px;">
    <div id="${ids.page}" style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 12 : 10}px;width:100%;max-width:${cardW}px;">

      <!-- Eyebrow -->
      <div id="${ids.eyebrow}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowSize}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">
        <span style="display:inline-block;width:34px;height:2px;background:${accent};"></span>
        <span>${escapeHtml(kategorieTag)}</span>
        <span style="display:inline-block;width:34px;height:2px;background:${accent};"></span>
      </div>

      <!-- Mega price hero -->
      <div id="${ids.price}" style="position:relative;display:inline-block;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${megaPriceSize}px;line-height:0.95;letter-spacing:-3px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.7)} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;text-align:center;text-shadow:0 0 60px ${withAlpha(accent, 0.4)};">
        ${escapeHtml(priceLabel)}
        <span id="${ids.priceUnderline}" style="display:block;height:4px;width:${Math.round(megaPriceSize * 0.6)}px;margin:8px auto 0;background:linear-gradient(90deg, ${accent}, ${withAlpha(accent, 0.2)});border-radius:9999px;box-shadow:0 0 18px ${withAlpha(accent, 0.7)};"></span>
      </div>

      <!-- Editorial title -->
      <div id="${ids.title}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${titleSize}px;line-height:1.15;letter-spacing:-0.5px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:80%;">${escapeHtml(title)}</div>

      <!-- Polaroid / print frame -->
      <div id="${ids.frame}" style="position:relative;margin-top:${isVertical ? 8 : 6}px;width:${isVertical ? 92 : 75}%;max-width:${isVertical ? 720 : 820}px;transform:rotate(-1.2deg);transform-origin:center;">
        <div id="${ids.framePaper}" style="position:relative;padding:18px 18px 28px;background:linear-gradient(180deg, #f8f8f5 0%, #efefe9 100%);border-radius:8px;box-shadow:0 30px 80px rgba(0,0,0,0.55), 0 2px 0 rgba(0,0,0,0.06) inset;">
          <div style="position:relative;width:100%;height:${imgH}px;border-radius:4px;overflow:hidden;background:#1a1d24;">
            ${buildProductImage(ids.img, imgH)}
          </div>
          <div style="margin-top:14px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${footSize}px;letter-spacing:3px;text-transform:uppercase;color:rgba(20,20,30,0.55);text-align:center;">
            ${escapeHtml(sellerName)}${locationLabel ? ` · ${escapeHtml(locationLabel)}` : ""}
          </div>
        </div>
        <!-- accent tape -->
        <div style="position:absolute;top:-14px;left:50%;transform:translateX(-50%) rotate(-3deg);width:120px;height:26px;background:${withAlpha(accent, 0.85)};opacity:0.92;box-shadow:0 4px 10px rgba(0,0,0,0.25);"></div>
      </div>

      ${descShort ? `<div id="${ids.desc}" style="margin-top:${isVertical ? 8 : 6}px;font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${descSize}px;line-height:1.5;color:${withAlpha(themeColors.fg, 0.8)};text-align:center;text-wrap:balance;max-width:78%;column-gap:32px;${(!isVertical && descShort.length > 180) ? "columns:2;" : ""}">${escapeHtml(descShort)}</div>` : ""}

      <!-- Footer rule + CTA -->
      <div id="${ids.foot}" style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 12 : 10}px;margin-top:${isVertical ? 10 : 6}px;">
        <div id="${ids.footRule}" style="width:80px;height:1px;background:${withAlpha(themeColors.fg, 0.35)};"></div>
        <div id="${ids.cta}" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:${isVertical ? 16 : 13}px ${isVertical ? 30 : 26}px;border-radius:9999px;background:transparent;border:2px solid ${accent};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2.5px;text-transform:uppercase;color:${themeColors.fg};">
          <div id="${ids.ctaGlow}" style="position:absolute;inset:-12px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.45)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
          <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          <span>${escapeHtml(ctaText)}</span>
        </div>
      </div>

    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${ids.page}`, { opacity: 0 }),
    gsapSetLine(`#${ids.eyebrow}`, { opacity: 0, y: 10 }),
    gsapSetLine(`#${ids.price}`, { opacity: 0, y: 30, scale: 0.85 }),
    gsapSetLine(`#${ids.priceUnderline}`, { scaleX: 0, transformOrigin: "0% 50%" }),
    gsapSetLine(`#${ids.title}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${ids.frame}`, { opacity: 0, y: 30, rotation: -6 }),
    gsapSetLine(`#${ids.foot}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${ids.cta}`, { opacity: 0, scale: 0.92 }),
    gsapSetLine(`#${ids.ctaGlow}`, { opacity: 0 }),
  ].join("\n");

  const tweens: string[] = [
    t.to(`#${ids.page}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, 0),
    t.to(`#${ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.15),
    t.to(`#${ids.price}`, { opacity: 1, y: 0, scale: 1, duration: 0.85, ease: "raw:\"back.out(1.4)\"" }, 0.35),
    t.to(`#${ids.priceUnderline}`, { scaleX: 1, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.85),
    t.to(`#${ids.title}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.65),
    t.to(`#${ids.frame}`, { opacity: 1, y: 0, rotation: -1.2, duration: 0.85, ease: "raw:\"back.out(1.5)\"" }, 0.85),
    t.to(`#${ids.foot}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.45),
    t.to(`#${ids.cta}`, { opacity: 1, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, 1.55),
    t.to(`#${ids.ctaGlow}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.7),
    t.to(`#${ids.ctaGlow}`, { opacity: 0.35, duration: 1.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 2.3),
    // Gentle floating of the polaroid frame
    t.to(`#${ids.frame}`, { y: -8, duration: 2.4, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 2.0),
  ];

  return { html, init, tweens };
}

// ============================================================================
// LAYOUT 4 — CARD (Allyver-CI · ONE elegant glass-card · zentriert · "Reveal"-DNA)
// ============================================================================
// Eine einzige große Glass-Card mit Stempel-Pill oben am Rand — direkter
// stilistischer Counterpart zu `partnerReveal` / `partnerComingSoon`:
//   • Card-Rahmen (Glass + Border + Glow), 36 px Radius — wie Reveal-Card.
//   • PRODUKT-Stempel-Pill auf der OBEREN Card-Kante (statt JETZT-LIVE-Stempel).
//   • Bild im großen Top-Slot (gleiche Card-DNA wie der Logo-Slot bei Reveal).
//   • Titel groß + Preis als Akzent-Gradient.
//   • Verkäufer-Mini-Card + Standort als kompakte Info-Zeile.
//   • Footer-Linie "— ERHÄLTLICH AUF ALLYVER —" (gleiche DNA wie Reveal-Footer).
//   • CTA-Pill am unteren Rand.
// Komplett vertikal zentriert im Safe-Area, gleiche Padding-Werte wie Reveal.
// ============================================================================
function buildCard(L: LayoutCtx): Fragment {
  const { clipPrefix, isVertical, accent, ctx, t, title, sellerName, priceLabel,
    locationLabel, kategorieTag, descShort, ctaText, url, sellerLogo,
    buildProductImage, productImg } = L;

  // Card-Geometrie 1:1 vom Reveal/ComingSoon-Counterpart, etwas größer für mehr Inhalt
  const cardW = isVertical ? 940 : 760;
  const imgArea = isVertical ? 480 : 360;

  // Sizes
  const stampSize  = typeSize("md", ctx.format) * 0.78;
  const tagSize    = typeSize("xs", ctx.format) * 0.85;
  const titleSize  = typeSize(isVertical ? "lg" : "md", ctx.format) * 0.85;
  const priceSize  = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.4;
  const sellerSize = typeSize("sm", ctx.format) * 0.8;
  const locSize    = typeSize("sm", ctx.format) * 0.72;
  const descSize   = typeSize("sm", ctx.format) * 0.78;
  const ctaSize    = typeSize("sm", ctx.format) * 0.82;
  const footSize   = typeSize("sm", ctx.format) * 0.78;
  void url;
  const sellerAvatarSize = isVertical ? 44 : 40;

  // Etwas weniger pad-top/bottom + kompakteres Card-Padding gibt mehr Platz für Inhalt
  const padTopBot = isVertical ? 50 : 40;
  const cardPadV  = isVertical ? 44 : 36;
  const cardPadH  = isVertical ? 40 : 34;

  const ids = {
    pad:        `${clipPrefix}-pad`,
    card:       `${clipPrefix}-card`,
    cardGlow:   `${clipPrefix}-card-glow`,
    stampWrap:  `${clipPrefix}-stamp-wrap`,
    stamp:      `${clipPrefix}-stamp`,
    imgSlot:    `${clipPrefix}-img-slot`,
    img:        `${clipPrefix}-img`,
    imgGloss:   `${clipPrefix}-img-gloss`,
    title:      `${clipPrefix}-title`,
    price:      `${clipPrefix}-price`,
    priceUnder: `${clipPrefix}-price-under`,
    seller:     `${clipPrefix}-seller`,
    sellerAv:   `${clipPrefix}-sellerav`,
    loc:        `${clipPrefix}-loc`,
    desc:       `${clipPrefix}-desc`,
    cta:        `${clipPrefix}-cta`,
    ctaGlow:    `${clipPrefix}-cta-glow`,
    footer:     `${clipPrefix}-foot`,
  };

  // Verkäufer-Avatar (selbe DNA wie stacked)
  const sellerAvatarHtml = sellerLogo
    ? `<img id="${ids.sellerAv}" src="${escapeHtml(sellerLogo)}" alt="seller" style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:11px;object-fit:contain;background:${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};padding:4px;flex:0 0 auto;" />`
    : `<div id="${ids.sellerAv}" style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:11px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.6)} 100%);display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sellerAvatarSize * 0.5}px;color:${themeColors.bg};flex:0 0 auto;">${escapeHtml(sellerName.charAt(0))}</div>`;

  // PRODUKT-Stempel-Pill (oben auf der Card-Kante — mirror der Reveal-Stamp-DNA)
  const stampHtml = `<div id="${ids.stampWrap}" style="position:absolute;top:-22px;left:50%;z-index:5;">
    <div id="${ids.stamp}" style="position:relative;display:inline-flex;align-items:center;gap:10px;padding:12px 24px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 12px 36px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${stampSize}px;letter-spacing:4px;text-transform:uppercase;color:${themeColors.bg};">
      <span style="display:inline-block;width:9px;height:9px;border-radius:9999px;background:${themeColors.bg};box-shadow:0 0 10px ${themeColors.bg};animation:hyper-blink 1.6s ease-in-out infinite;"></span>
      <span>${escapeHtml(kategorieTag)}</span>
    </div>
  </div>`;

  // Footer-Linie (mirror der Reveal-Footer-DNA) — fixe Allyver-Plattform-Stempel,
  // unabhängig vom CTA-Pill-Text. Hält das Reveal-Branding konsistent.
  const footerHtml = `<div id="${ids.footer}" style="display:inline-flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${footSize}px;letter-spacing:5px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.85)};">
    <span style="width:28px;height:2px;background:${accent};"></span>
    <span>Erhältlich auf Allyver</span>
    <span style="width:28px;height:2px;background:${accent};"></span>
  </div>`;

  const html = `<style>@keyframes hyper-blink{0%,100%{opacity:0.45;}50%{opacity:1;}}</style>
<div id="${ids.pad}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:28px;padding:${padTopBot}px 60px;">

  <div id="${ids.card}" style="position:relative;width:${cardW}px;max-width:92%;border-radius:36px;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.015)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};box-shadow:0 40px 100px rgba(0,0,0,0.55), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.16)};backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);padding:${cardPadV}px ${cardPadH}px;display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 18 : 14}px;">
    <div id="${ids.cardGlow}" aria-hidden="true" style="position:absolute;inset:-2px;border-radius:38px;background:linear-gradient(135deg, ${withAlpha(accent, 0.55)} 0%, transparent 35%, transparent 65%, ${withAlpha(accent, 0.4)} 100%);opacity:0;pointer-events:none;mix-blend-mode:screen;filter:blur(2px);"></div>

    ${stampHtml}

    <!-- BILD-SLOT (gleiche DNA wie Logo-Slot im Reveal) -->
    <div id="${ids.imgSlot}" style="position:relative;width:100%;height:${imgArea}px;border-radius:22px;overflow:hidden;background:${withAlpha(themeColors.fg, 0.04)};border:1px solid ${withAlpha(themeColors.fg, 0.12)};">
      ${buildProductImage(ids.img, imgArea)}
      <div id="${ids.imgGloss}" style="position:absolute;top:0;left:0;right:0;height:32%;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.16)} 0%, transparent 100%);pointer-events:none;mix-blend-mode:overlay;"></div>
    </div>

    <!-- TITEL (zentriert, max 2 Zeilen) -->
    <div id="${ids.title}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.08;letter-spacing:-1.2px;color:${themeColors.fg};text-align:center;text-wrap:balance;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-shadow:0 0 22px ${withAlpha(accent, 0.18)};">${escapeHtml(title)}</div>

    <!-- PREIS (Hero, gradient, underline — DNA aus stacked layout) -->
    <div id="${ids.price}" style="position:relative;display:inline-block;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${priceSize}px;line-height:1;letter-spacing:-1.5px;background:linear-gradient(135deg, ${accent} 0%, ${accent} 55%, ${withAlpha(accent, 0.78)} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;padding:2px 0;">
      ${escapeHtml(priceLabel)}
      <span id="${ids.priceUnder}" style="position:absolute;left:50%;bottom:-6px;width:60px;height:3px;margin-left:-30px;border-radius:9999px;background:linear-gradient(90deg, ${withAlpha(accent, 0.4)}, ${accent}, ${withAlpha(accent, 0.4)});box-shadow:0 0 14px ${withAlpha(accent, 0.55)};transform-origin:50% 50%;"></span>
    </div>

    <!-- VERKÄUFER-CARD (kompakt, zentriert) -->
    <div id="${ids.seller}" style="display:inline-flex;align-items:center;gap:12px;padding:10px 18px;border-radius:14px;background:${withAlpha(themeColors.fg, 0.05)};border:1px solid ${withAlpha(themeColors.fg, 0.12)};">
      ${sellerAvatarHtml}
      <div style="display:flex;flex-direction:column;gap:2px;text-align:left;">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sellerSize}px;color:${themeColors.fg};line-height:1.1;">${escapeHtml(sellerName)}</div>
        ${locationLabel ? `<div id="${ids.loc}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${locSize}px;color:${withAlpha(themeColors.fg, 0.62)};display:flex;align-items:center;gap:5px;">
          <svg width="${Math.round(locSize * 1.05)}" height="${Math.round(locSize * 1.05)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span>${escapeHtml(locationLabel)}</span>
        </div>` : `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${locSize}px;color:${withAlpha(themeColors.fg, 0.55)};letter-spacing:1.4px;text-transform:uppercase;">Anbieter</div>`}
      </div>
    </div>

    ${descShort ? `<div id="${ids.desc}" style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${descSize}px;line-height:1.45;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;text-wrap:balance;max-width:90%;display:-webkit-box;-webkit-line-clamp:${isVertical ? 3 : 2};-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(descShort)}</div>` : ""}

    <!-- CTA-Pill (mirror der Reveal-Pill-DNA) — generische CTA, kein URL-Text -->
    <div id="${ids.cta}" style="position:relative;display:inline-flex;align-items:center;gap:12px;padding:${isVertical ? 16 : 13}px ${isVertical ? 30 : 24}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 16px 40px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2px;text-transform:uppercase;color:${themeColors.bg};">
      <div id="${ids.ctaGlow}" style="position:absolute;inset:-12px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
      <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>
      <span style="white-space:nowrap;">${escapeHtml(ctaText)}</span>
    </div>
  </div>

  ${footerHtml}
</div>`;

  void productImg;

  const initLines: string[] = [
    gsapSetLine(`#${ids.card}`, { opacity: 0, y: 60, scale: 0.94 }),
    gsapSetLine(`#${ids.cardGlow}`, { opacity: 0 }),
    gsapSetLine(`#${ids.stampWrap}`, { opacity: 0, scale: 0.4, rotation: 0, xPercent: -50 }),
    gsapSetLine(`#${ids.imgSlot}`, { opacity: 0, scale: 0.94 }),
    gsapSetLine(`#${ids.title}`, { opacity: 0, y: 14 }),
    gsapSetLine(`#${ids.price}`, { opacity: 0, y: 18, scale: 0.96 }),
    gsapSetLine(`#${ids.priceUnder}`, { scaleX: 0 }),
    gsapSetLine(`#${ids.seller}`, { opacity: 0, y: 12 }),
    gsapSetLine(`#${ids.cta}`, { opacity: 0, y: 18, scale: 0.92 }),
    gsapSetLine(`#${ids.ctaGlow}`, { opacity: 0 }),
    gsapSetLine(`#${ids.footer}`, { opacity: 0, y: 12 }),
  ];
  if (descShort) initLines.push(gsapSetLine(`#${ids.desc}`, { opacity: 0, y: 10 }));

  const tweens: string[] = [
    t.to(`#${ids.card}`, { opacity: 1, y: 0, scale: 1, duration: 0.95, ease: "raw:\"power3.out\"" }, 0.05),
    t.fromTo(`#${ids.cardGlow}`, { opacity: 0 }, { opacity: 0.55, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${ids.cardGlow}`, { opacity: 0.18, duration: 0.7, ease: "raw:\"power2.inOut\"" }, 0.85),
    t.to(`#${ids.imgSlot}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"back.out(1.4)\"" }, 0.5),
    t.to(`#${ids.stampWrap}`, { opacity: 1, scale: 1, rotation: 0, duration: 0.6, ease: "raw:\"back.out(2.2)\"" }, 0.7),
    t.to(`#${ids.title}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.95),
    t.to(`#${ids.price}`, { opacity: 1, y: 0, scale: 1, duration: 0.65, ease: "raw:\"back.out(1.5)\"" }, 1.15),
    t.to(`#${ids.priceUnder}`, { scaleX: 1, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.5),
    t.to(`#${ids.seller}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 1.4),
  ];
  if (descShort) tweens.push(t.to(`#${ids.desc}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.6));
  const ctaAt = descShort ? 1.9 : 1.7;
  tweens.push(t.to(`#${ids.cta}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, ctaAt));
  tweens.push(t.to(`#${ids.ctaGlow}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, ctaAt + 0.1));
  tweens.push(t.to(`#${ids.ctaGlow}`, { opacity: 0.4, duration: 1.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 0.7));
  tweens.push(t.to(`#${ids.footer}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, ctaAt + 0.3));
  // Sanftes Schweben am Ende (genau wie Reveal)
  tweens.push(t.to(`#${ids.card}`, { y: -6, duration: 2.4, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 1.2));

  return { html, init: initLines.join("\n"), tweens };
}

// ============================================================================
// LAYOUT 5 — SPLIT (Card-Frame mit harter Bild/Info-Trennung · Allyver-CI · zentriert)
// ============================================================================
// Eine einzige zentrierte Card (gleiche Glass-DNA wie Reveal-Card). Innerhalb
// der Card harte 50/50-Trennung mit Accent-Glow-Linie:
//   9:16 + 1:1 → Bild oben, Info unten
//   16:9       → Bild links (45 %), Info rechts (55 %)
// Card-Dimensionen sind so dimensioniert, dass sie GARANTIERT in den Safe-Area
// passen — CTA bleibt sichtbar, nichts wird abgeschnitten.
// ============================================================================
function buildSplit(L: LayoutCtx): Fragment {
  const { clipPrefix, isVertical, accent, ctx, t, title, sellerName, priceLabel,
    locationLabel, kategorieTag, descShort, ctaText, url, sellerLogo,
    buildProductImage } = L;
  void url;

  const isWide = ctx.format === "16:9";

  // Card-Geometrie — nutzt nahezu den ganzen Reels-Safe-Area aus
  // (9:16 Reels-Safe ≈ 1260 px → cardH=1260 mit padTopBot=30 lässt knapp Puffer).
  const cardW = ctx.format === "16:9" ? 1620 : ctx.format === "1:1" ? 900 : 960;
  const cardH = ctx.format === "16:9" ? 820  : ctx.format === "1:1" ? 900 : 1260;

  // Sizes — Titel adaptiv: langer Titel bekommt kleinere Schrift, damit er
  // IMMER in 2 Zeilen passt und nichts mehr abgeschnitten ist.
  const titleLen = title.length;
  const titleScale = titleLen > 38 ? 0.62 : titleLen > 26 ? 0.72 : titleLen > 16 ? 0.82 : 0.92;
  const tagSize    = typeSize("xs", ctx.format) * 0.85;
  const titleSize  = typeSize(isVertical ? "lg" : "md", ctx.format) * titleScale;
  const priceSize  = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.42;
  const sellerSize = typeSize("sm", ctx.format) * 0.78;
  const locSize    = typeSize("sm", ctx.format) * 0.7;
  const descSize   = typeSize("sm", ctx.format) * 0.74;
  const ctaSize    = typeSize("sm", ctx.format) * 0.8;
  const sellerAvatarSize = isVertical ? 40 : 36;
  const padTopBot = isVertical ? 30 : 30;

  const ids = {
    pad:    `${clipPrefix}-pad`,
    card:   `${clipPrefix}-card`,
    cardGlow: `${clipPrefix}-cardglow`,
    edge:   `${clipPrefix}-edge`,
    imgZone:`${clipPrefix}-imgz`,
    img:    `${clipPrefix}-img`,
    imgGloss: `${clipPrefix}-igloss`,
    infoZone: `${clipPrefix}-info`,
    pill:   `${clipPrefix}-pill`,
    title:  `${clipPrefix}-title`,
    rule:   `${clipPrefix}-rule`,
    price:  `${clipPrefix}-price`,
    seller: `${clipPrefix}-seller`,
    loc:    `${clipPrefix}-loc`,
    desc:   `${clipPrefix}-desc`,
    cta:    `${clipPrefix}-cta`,
    ctaGlow:`${clipPrefix}-cglow`,
  };

  const sellerAvatarHtml = sellerLogo
    ? `<img src="${escapeHtml(sellerLogo)}" alt="seller" style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:10px;object-fit:contain;background:${withAlpha(themeColors.fg, 0.08)};border:1px solid ${withAlpha(themeColors.fg, 0.18)};padding:4px;flex:0 0 auto;" />`
    : `<div style="width:${sellerAvatarSize}px;height:${sellerAvatarSize}px;border-radius:10px;background:linear-gradient(135deg, ${accent}, ${withAlpha(accent, 0.6)});display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-weight:${weights.black};font-size:${sellerAvatarSize * 0.5}px;flex:0 0 auto;font-family:'Outfit',sans-serif;">${escapeHtml(sellerName.charAt(0))}</div>`;

  const flexDir   = isWide ? "row" : "column";
  const imgBasis  = isWide ? "45%" : "50%";
  const infoBasis = isWide ? "55%" : "50%";
  const imgPad    = isWide ? "24px 0 24px 24px" : "24px 24px 0 24px";
  const infoPad   = isWide ? "30px 32px" : (isVertical ? "26px 30px 30px 30px" : "24px 28px");

  const html = `<div id="${ids.pad}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:${padTopBot}px 60px;">
    <div id="${ids.card}" style="position:relative;width:${cardW}px;max-width:92%;height:${cardH}px;max-height:calc(100% - ${padTopBot * 2}px);border-radius:36px;overflow:hidden;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.015)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};box-shadow:0 40px 100px rgba(0,0,0,0.55), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.16)};backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);display:flex;flex-direction:${flexDir};">

      <!-- Card-Glow (gleiche DNA wie Reveal-Card) -->
      <div id="${ids.cardGlow}" aria-hidden="true" style="position:absolute;inset:-2px;border-radius:38px;background:linear-gradient(135deg, ${withAlpha(accent, 0.5)} 0%, transparent 35%, transparent 65%, ${withAlpha(accent, 0.4)} 100%);opacity:0;pointer-events:none;mix-blend-mode:screen;filter:blur(2px);"></div>

      <!-- Accent-Glow-Linie an der Trennkante zwischen Bild und Info -->
      <div id="${ids.edge}" style="position:absolute;${isWide ? `top:24px;bottom:24px;left:calc(${imgBasis} - 1.5px);width:3px;` : `left:24px;right:24px;top:calc(${imgBasis} - 1.5px);height:3px;`};background:linear-gradient(${isWide ? "180deg" : "90deg"}, transparent 0%, ${accent} 30%, ${accent} 70%, transparent 100%);box-shadow:0 0 18px ${withAlpha(accent, 0.7)};border-radius:9999px;z-index:5;transform-origin:50% 50%;"></div>

      <!-- BILD-ZONE -->
      <div id="${ids.imgZone}" style="position:relative;flex:0 0 ${imgBasis};${isWide ? "height:100%;" : "width:100%;"};padding:${imgPad};box-sizing:border-box;">
        <div style="position:relative;width:100%;height:100%;border-radius:22px;overflow:hidden;background:${withAlpha(themeColors.fg, 0.05)};">
          ${buildProductImage(ids.img, isWide ? cardH - 48 : Math.round(cardH / 2 - 24))}
          <div id="${ids.imgGloss}" style="position:absolute;top:0;left:0;right:0;height:32%;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.16)} 0%, transparent 100%);pointer-events:none;mix-blend-mode:overlay;"></div>
        </div>
      </div>

      <!-- INFO-ZONE -->
      <div id="${ids.infoZone}" style="position:relative;flex:1 1 ${infoBasis};${isWide ? "height:100%;" : "width:100%;"};padding:${infoPad};box-sizing:border-box;display:flex;flex-direction:column;gap:${isVertical ? 10 : 9}px;overflow:hidden;">

        <div id="${ids.pill}" style="display:inline-flex;align-self:flex-start;align-items:center;gap:8px;padding:5px 13px;border-radius:9999px;background:${withAlpha(accent, 0.18)};border:1px solid ${withAlpha(accent, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${tagSize}px;letter-spacing:3px;text-transform:uppercase;color:${accent};">
          <span style="display:inline-block;width:6px;height:6px;border-radius:9999px;background:${accent};box-shadow:0 0 10px ${accent};"></span>
          <span>${escapeHtml(kategorieTag)}</span>
        </div>

        <div id="${ids.title}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.12;letter-spacing:-1px;color:${themeColors.fg};text-wrap:balance;word-break:break-word;hyphens:auto;flex:0 0 auto;">${escapeHtml(title)}</div>

        <div id="${ids.rule}" style="height:1px;background:linear-gradient(90deg, ${withAlpha(accent, 0.5)} 0%, ${withAlpha(themeColors.fg, 0.15)} 70%, transparent 100%);transform-origin:0% 50%;"></div>

        <div id="${ids.price}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${priceSize}px;line-height:1;letter-spacing:-1.5px;background:linear-gradient(135deg, ${accent} 0%, ${accent} 55%, ${withAlpha(accent, 0.78)} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;">${escapeHtml(priceLabel)}</div>

        <div id="${ids.seller}" style="display:flex;align-items:center;gap:10px;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sellerSize}px;color:${withAlpha(themeColors.fg, 0.78)};flex-wrap:wrap;">
          ${sellerAvatarHtml}
          <span style="color:${themeColors.fg};">${escapeHtml(sellerName)}</span>
          ${locationLabel ? `<span style="opacity:0.4;">·</span>
          <span id="${ids.loc}" style="display:inline-flex;align-items:center;gap:5px;font-size:${locSize}px;">
            <svg width="${Math.round(locSize)}" height="${Math.round(locSize)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>${escapeHtml(locationLabel)}</span>
          </span>` : ""}
        </div>

        ${descShort ? `<div id="${ids.desc}" style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${descSize}px;line-height:1.4;color:${withAlpha(themeColors.fg, 0.72)};text-wrap:balance;display:-webkit-box;-webkit-line-clamp:${isWide ? 5 : 4};-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(descShort)}</div>` : ""}

        <!-- CTA-Pill — generischer Call-to-Action, kein URL-Text -->
        <div id="${ids.cta}" style="position:relative;display:inline-flex;align-items:center;gap:10px;padding:${isVertical ? 14 : 12}px ${isVertical ? 26 : 22}px;margin-top:auto;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 14px 36px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2px;text-transform:uppercase;color:${themeColors.bg};max-width:100%;align-self:flex-start;">
          <div id="${ids.ctaGlow}" style="position:absolute;inset:-12px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
          <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>
          <span style="white-space:nowrap;">${escapeHtml(ctaText)}</span>
        </div>
      </div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${ids.card}`, { opacity: 0, y: 40, scale: 0.95 }),
    gsapSetLine(`#${ids.cardGlow}`, { opacity: 0 }),
    gsapSetLine(`#${ids.edge}`, { scaleY: isWide ? 0 : 1, scaleX: isWide ? 1 : 0 }),
    gsapSetLine(`#${ids.imgZone}`, { opacity: 0 }),
    gsapSetLine(`#${ids.img}`, { scale: 1.12 }),
    gsapSetLine(`#${ids.pill}`, { opacity: 0, y: 10 }),
    gsapSetLine(`#${ids.title}`, { opacity: 0, y: 14 }),
    gsapSetLine(`#${ids.rule}`, { scaleX: 0 }),
    gsapSetLine(`#${ids.price}`, { opacity: 0, y: 18, scale: 0.94 }),
    gsapSetLine(`#${ids.seller}`, { opacity: 0, y: 10 }),
    gsapSetLine(`#${ids.cta}`, { opacity: 0, y: 18, scale: 0.92 }),
    gsapSetLine(`#${ids.ctaGlow}`, { opacity: 0 }),
  ];
  if (descShort) init.push(gsapSetLine(`#${ids.desc}`, { opacity: 0, y: 10 }));

  const tweens: string[] = [
    t.to(`#${ids.card}`, { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: "raw:\"power3.out\"" }, 0.05),
    t.fromTo(`#${ids.cardGlow}`, { opacity: 0 }, { opacity: 0.5, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${ids.cardGlow}`, { opacity: 0.18, duration: 0.8, ease: "raw:\"power2.inOut\"" }, 0.85),
    t.to(`#${ids.imgZone}`, { opacity: 1, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.35),
    t.to(`#${ids.img}`, { scale: 1.0, duration: 5.5, ease: "raw:\"power1.out\"" }, 0.35),
    t.to(`#${ids.edge}`, { scaleY: 1, scaleX: 1, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.6),
    t.to(`#${ids.pill}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 0.8),
    t.to(`#${ids.title}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 0.95),
    t.to(`#${ids.rule}`, { scaleX: 1, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.15),
    t.to(`#${ids.price}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.6)\"" }, 1.3),
    t.to(`#${ids.seller}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power3.out\"" }, 1.55),
  ];
  if (descShort) tweens.push(t.to(`#${ids.desc}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 1.75));
  const ctaAt = descShort ? 2.05 : 1.8;
  tweens.push(t.to(`#${ids.cta}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "raw:\"back.out(1.6)\"" }, ctaAt));
  tweens.push(t.to(`#${ids.ctaGlow}`, { opacity: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, ctaAt + 0.1));
  tweens.push(t.to(`#${ids.ctaGlow}`, { opacity: 0.4, duration: 1.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 0.7));
  // Sanftes Schweben am Ende (DNA wie Reveal)
  tweens.push(t.to(`#${ids.card}`, { y: -5, duration: 2.4, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, ctaAt + 1.2));

  return { html, init: init.join("\n"), tweens };
}
